import { useState, useEffect, useCallback } from "react";
import { useMutation } from "convex/react";
import { api } from "@/convex/src/_generated/api";
import type { Id } from "@/convex/src/_generated/dataModel";

/**
 * useInvitationHandler
 *
 * Handles invitation token preservation across SSO redirects
 * Manages localStorage for token storage and cleanup
 * Handles structured errors and responses from backend
 */

const STORAGE_KEY = "pending-invite-token";
const MAX_TOKEN_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface StoredToken {
  token: string;
  timestamp: number;
}

export interface InvitationError {
  code: string;
  message: string;
  details?: {
    invitedEmail?: string;
    authenticatedEmail?: string;
    expiresAt?: number;
    [key: string]: any;
  };
}

export interface InvitationResult {
  workspaceId: Id<"appWorkspaces">;
  action:
    | "joined"
    | "upgraded"
    | "already_member"
    | "already_member_higher_role";
  role?: string;
  oldRole?: string;
  newRole?: string;
  currentRole?: string;
  invitedRole?: string;
}

interface InvitationState {
  isProcessing: boolean;
  error: InvitationError | null;
  result: InvitationResult | null;
}

export function useInvitationHandler() {
  const [state, setState] = useState<InvitationState>({
    isProcessing: false,
    error: null,
    result: null,
  });

  const acceptInvitationMutation = useMutation(
    api.workspaceMembers.acceptInvitation,
  );

  /**
   * Store invitation token in localStorage
   */
  const storeInvitationToken = useCallback((token: string) => {
    const stored: StoredToken = {
      token,
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  }, []);

  /**
   * Retrieve invitation token from localStorage
   * Returns null if not found or expired
   */
  const getStoredToken = useCallback((): string | null => {
    const storedData = localStorage.getItem(STORAGE_KEY);
    if (!storedData) return null;

    try {
      const stored: StoredToken = JSON.parse(storedData);
      const age = Date.now() - stored.timestamp;

      // Check if token has expired
      if (age > MAX_TOKEN_AGE_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return stored.token;
    } catch (error) {
      // Invalid JSON, clean up
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  }, []);

  /**
   * Clear stored invitation token
   */
  const clearStoredToken = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  /**
   * Parse structured error from backend
   */
  const parseError = (error: any): InvitationError => {
    try {
      // Try to parse JSON error
      const parsed = JSON.parse(error.message);
      if (parsed.code && parsed.message) {
        return parsed;
      }
    } catch {
      // Not JSON, use as plain error
    }

    // Fallback to plain error
    return {
      code: "UNKNOWN_ERROR",
      message: error.message || "Failed to accept invitation",
    };
  };

  /**
   * Accept an invitation token
   * Returns structured result with action taken
   */
  const acceptInvitation = useCallback(
    async (token: string): Promise<InvitationResult | null> => {
      setState({ isProcessing: true, error: null, result: null });

      try {
        const result = (await acceptInvitationMutation({
          token,
        })) as InvitationResult;
        setState({ isProcessing: false, error: null, result });
        clearStoredToken();
        return result;
      } catch (error: any) {
        const parsedError = parseError(error);
        setState({ isProcessing: false, error: parsedError, result: null });
        clearStoredToken(); // Clear even on error to avoid retry loops
        throw parsedError;
      }
    },
    [acceptInvitationMutation, clearStoredToken],
  );

  /**
   * Check URL for invitation token and store it
   * Returns true if token was found and stored
   */
  const checkAndStoreUrlToken = useCallback((): boolean => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("invite");

    if (token) {
      storeInvitationToken(token);
      return true;
    }

    return false;
  }, [storeInvitationToken]);

  /**
   * Clear invitation token from URL
   */
  const clearUrlToken = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("invite")) {
      urlParams.delete("invite");
      const newUrl =
        window.location.pathname +
        (urlParams.toString() ? "?" + urlParams.toString() : "");
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  /**
   * Process stored invitation after authentication
   * Returns the invitation result if successful
   */
  const processStoredInvitation =
    useCallback(async (): Promise<InvitationResult | null> => {
      const token = getStoredToken();
      if (!token) return null;

      try {
        return await acceptInvitation(token);
      } catch (error) {
        console.error("Failed to process stored invitation:", error);
        return null;
      }
    }, [getStoredToken, acceptInvitation]);

  /**
   * Cleanup expired tokens on mount
   */
  useEffect(() => {
    getStoredToken(); // This will clean up expired tokens as a side effect
  }, [getStoredToken]);

  return {
    // State
    isProcessing: state.isProcessing,
    error: state.error,
    result: state.result,

    // Actions
    storeInvitationToken,
    getStoredToken,
    clearStoredToken,
    acceptInvitation,
    checkAndStoreUrlToken,
    clearUrlToken,
    processStoredInvitation,
  };
}
